// material-ui
import { useTheme, styled } from '@mui/material/styles';
import {
    Avatar,
    Box,
    // Chip,
    CircularProgress,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography
} from '@mui/material';

// third-party
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

// project imports
import type { NotificationItem } from '@/types/notification';

// assets
import { IconBell } from '@tabler/icons-react';

dayjs.extend(relativeTime);

const ListItemWrapper = styled('div', {
    shouldForwardProp: (prop) => prop !== '$unread'
})<{ $unread?: boolean }>(({ theme, $unread }) => ({
    cursor: 'pointer',
    padding: 16,
    backgroundColor: $unread ? (theme.palette.mode === 'dark' ? theme.palette.dark.dark : theme.palette.grey[200]) : 'transparent',
    '&:hover': {
        backgroundColor: $unread
            ? theme.palette.mode === 'dark'
                ? theme.palette.dark.main
                : theme.palette.grey[300]
            : theme.palette.mode === 'dark'
              ? theme.palette.dark.main
              : theme.palette.primary.light
    },
    '& .MuiListItem-root': {
        padding: 0
    }
}));

interface NotificationListProps {
    items: NotificationItem[];
    loading: boolean;
    error: string | null;
    markingAsReadId: string | null;
    onMarkAsRead: (id: string) => void;
    onItemClick?: (notification: NotificationItem) => void;
}

const formatRelativeTime = (value: string) => {
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.fromNow() : '';
};

// ==============================|| NOTIFICATION LIST ITEM ||============================== //

const NotificationList = ({ items, loading, error, markingAsReadId, onMarkAsRead, onItemClick }: NotificationListProps) => {
    const theme = useTheme();
    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ px: 2, py: 3 }}>
                <Typography variant="body2" color="error">
                    {error}
                </Typography>
            </Box>
        );
    }

    if (items.length === 0) {
        return (
            <Box sx={{ px: 2, py: 3 }}>
                <Typography variant="body2" color="text.secondary">
                    No notifications
                </Typography>
            </Box>
        );
    }

    return (
        <List
            sx={{
                width: '100%',
                maxWidth: 350,
                py: 0,
                borderRadius: '10px',
                [theme.breakpoints.down('md')]: {
                    maxWidth: 350
                },
                '& .MuiListItemSecondaryAction-root': {
                    top: 22
                },
                '& .MuiDivider-root': {
                    my: 0,
                    borderBottomWidth: 1,
                    borderBottomStyle: 'solid',
                    borderColor: 'rgba(0, 0, 0, 0.12)'
                },
                '& .list-container': {
                    pl: 7
                }
            }}
        >
            {items.map((notification, index, { length }) => {
                const isUnread = notification.status === 'unread';
                const isMarkingAsRead = markingAsReadId === notification.id;
                const isMatchTransReady = notification.typeKey === 'meeting_transcript_matches_ready';

                return (
                    <Box key={notification.id}>
                        <ListItemWrapper
                            $unread={isUnread}
                            onClick={() => {
                                if (isUnread && !isMarkingAsRead) {
                                    onMarkAsRead(notification.id);
                                }
                                onItemClick?.(notification);
                            }}
                        >
                            <ListItem alignItems="center">
                                <ListItemAvatar>
                                    <Avatar
                                        sx={{
                                            color: theme.palette.primary.dark,
                                            backgroundColor:
                                                theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                                            border: theme.palette.mode === 'dark' ? '1px solid' : 'none',
                                            borderColor: theme.palette.primary.main
                                        }}
                                    >
                                        <IconBell stroke={1.5} size="1.3rem" />
                                    </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                    primary={
                                        <Typography variant="subtitle1">{`${notification.title}${isMatchTransReady ? ` for ${notification.hospital.name}` : ''}`}</Typography>
                                    }
                                />
                            </ListItem>
                            <Grid sx={{ display: 'flex', flexDirection: 'column' }} className="list-container">
                                <Grid>
                                    <Typography variant="subtitle2">{notification.message}</Typography>
                                </Grid>
                                <Grid sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Typography variant="caption" sx={{ display: 'block', textAlign: 'right' }} gutterBottom>
                                        {formatRelativeTime(notification.createdAt)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </ListItemWrapper>
                        {index < length - 1 && <Divider />}
                    </Box>
                );
            })}
        </List>
    );
};

export default NotificationList;
